import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast }                from "react-toastify";
import { apiClient }            from "../../api/apiServices";

// ─────────────────────────────────────────────────────────────
// STYLING PASS:
//  - Hero card rebuilt with the sheen-layer treatment already used on
//    SavingsPage.jsx's hero (consistency across the two savings
//    pages) plus a thicker, glowing-near-completion progress bar
//    matching GoalCard's updated treatment — previously this page's
//    hero and the list page's cards used two visually unrelated
//    progress-bar styles.
//  - NEW: an Auto-Save status card. Previously invisible anywhere on
//    this page despite goal.autoSave being fully tracked server-side
//    (enabled, amount, frequency, nextDebitDate, lastDebitDate) — a
//    user with auto-save on had no way to confirm it from this page
//    at all. Also surfaces the autoSave.enabled:true + amount:0 case
//    (confirmed from real goal data) as an actionable warning rather
//    than silently implying automated progress that isn't happening.
// ─────────────────────────────────────────────────────────────

// FIXED — goal.daysRemaining was being trusted directly from the API
// with no validation and showed wrong numbers (not correctly computed/
// recomputed server-side). Same fix as SavingsPage.jsx's GoalCard:
// compute from endDate directly, calendar-day rounded, floored at 0.
function daysRemainingFrom(endDate) {
  if (!endDate) return null;
  const end = new Date(endDate);
  const now = new Date();
  const endDay = new Date(end.getFullYear(), end.getMonth(), end.getDate());
  const nowDay = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const diffDays = Math.round((endDay - nowDay) / 86400000);
  return Math.max(diffDays, 0);
}

function AutoSaveStatusCard({ goal, onFix }) {
  const { autoSave } = goal;
  if (!autoSave?.enabled) return null;

  const isConfigured = Number(autoSave.amount) > 0;

  return (
    <div
      className="rounded-2xl p-4 border"
      style={{
        background: isConfigured ? "var(--color-info-light)" : "var(--color-warning-light)",
        borderColor: isConfigured ? "var(--color-info-border)" : "var(--color-warning-border)",
      }}
    >
      <div className="flex items-start gap-3">
        <div
          className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
          style={{ background: isConfigured ? "var(--color-info)" : "var(--color-warning)" }}
        >
          <i className={isConfigured ? "ri-repeat-line" : "ri-error-warning-line"} style={{ color: "#fff" }} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-sm" style={{ color: isConfigured ? "var(--color-info-text)" : "var(--color-warning-text)" }}>
            {isConfigured ? "Auto-save is active" : "Auto-save needs an amount"}
          </p>
          {isConfigured ? (
            <>
              <p className="text-xs mt-0.5" style={{ color: "var(--color-info-text)" }}>
                ₦{Number(autoSave.amount).toLocaleString()} moves into this goal {autoSave.frequency}.
              </p>
              {autoSave.nextDebitDate && (
                <p className="text-[11px] mt-1.5 opacity-80" style={{ color: "var(--color-info-text)" }}>
                  Next debit: {new Date(autoSave.nextDebitDate).toLocaleDateString("en-NG", { day: "numeric", month: "short", year: "numeric" })}
                  {autoSave.lastDebitDate && ` · Last: ${new Date(autoSave.lastDebitDate).toLocaleDateString("en-NG", { day: "numeric", month: "short" })}`}
                </p>
              )}
            </>
          ) : (
            <>
              <p className="text-xs mt-0.5" style={{ color: "var(--color-warning-text)" }}>
                Auto-save is turned on, but no amount is set — nothing is actually being moved {autoSave.frequency}.
              </p>
              {onFix && (
                <button onClick={onFix} className="text-xs font-bold mt-2 underline" style={{ color: "var(--color-warning-text)" }}>
                  Set an amount
                </button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default function SavingsGoalDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [goal, setGoal]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [action, setAction]   = useState(null);
  const [amount, setAmount]   = useState("");
  const [pin, setPin]         = useState("");
  const [busy, setBusy]       = useState(false);
  const [quote, setQuote]     = useState(null);

  const load = () => {
    apiClient.get(`/mtrade/savings/${id}`)
      .then(r => { if (r.data.status) setGoal(r.data.data); })
      .catch(() => toast.error("Could not load goal"))
      .finally(() => setLoading(false));
  };
  useEffect(load, [id]);

  const matured = goal && new Date() >= new Date(goal.endDate);
  const canWithdraw = goal && goal.status === "active";

  const openWithdraw = async () => {
    setQuote(null);
    setAction("withdraw");
    try {
      const r = await apiClient.get(`/mtrade/savings/${id}/withdraw-quote`);
      if (r.data.status) setQuote(r.data.data);
    } catch {}
  };

  const submitTopup = async () => {
    if (!amount || pin.length !== 4) return toast.error("Enter amount and 4-digit PIN");
    setBusy(true);
    try {
      const r = await apiClient.post(`/mtrade/savings/${id}/topup`, { amount: Number(amount), transactionPin: pin });
      if (r.data.status) { toast.success(r.data.message); setAction(null); setAmount(""); setPin(""); load(); }
      else toast.error(r.data.message);
    } catch (err) { toast.error(err?.response?.data?.message || "Top-up failed"); setPin(""); }
    finally { setBusy(false); }
  };

  const submitWithdraw = async () => {
    if (pin.length !== 4) return toast.error("Enter your 4-digit PIN");
    setBusy(true);
    try {
      const r = await apiClient.post(`/mtrade/savings/${id}/withdraw`, { transactionPin: pin });
      if (r.data.status) { toast.success(r.data.message); navigate("/savings"); }
      else toast.error(r.data.message);
    } catch (err) { toast.error(err?.response?.data?.message || "Withdrawal failed"); setPin(""); }
    finally { setBusy(false); }
  };

  if (loading) return (
    <div className="min-h-screen bg-bg flex items-center justify-center">
      <div className="w-10 h-10 border-4 border-t-transparent rounded-full animate-spin" style={{ borderColor: "var(--color-brand)", borderTopColor: "transparent" }} />
    </div>
  );
  if (!goal) return <div className="min-h-screen bg-bg flex items-center justify-center text-faint">Goal not found</div>;

  const pct = goal.progressPercent ?? 0;
  // FIXED — was goal.daysRemaining (unreliable, see file header note).
  const daysLeft = daysRemainingFrom(goal.endDate);
  const isNearDone = goal.status === "active" && pct >= 85;

  return (
    <div className="min-h-screen bg-bg pb-10">
      <div className="bg-surface px-5 pt-12 pb-4 border-b border-border">
        <div className="flex items-center gap-3 max-w-lg mx-auto">
          <button onClick={() => navigate("/savings")} className="w-9 h-9 rounded-xl bg-surface-hover flex items-center justify-center">
            <i className="ri-arrow-left-line text-secondary text-lg" />
          </button>
          <div>
            <h1 className="text-primary font-bold text-lg font-display">{goal.title}</h1>
            <p className="text-muted text-xs capitalize">{goal.type} savings · {goal.status}</p>
          </div>
        </div>
      </div>

      <div className="px-5 pt-5 pb-10 max-w-lg mx-auto space-y-5">
        {/* FIXED — sheen-layer hero, matching SavingsPage.jsx's
            treatment for visual consistency between the two pages,
            plus a thicker progress bar with a near-completion glow
            (same treatment as GoalCard). */}
        <div className="rounded-2xl overflow-hidden relative" style={{ boxShadow: "var(--gradient-hero-shadow)" }}>
          <div className="px-5 py-6 text-center relative" style={{ background: "var(--gradient-hero)" }}>
            <div className="absolute inset-0 pointer-events-none" style={{ background: "var(--gradient-hero-sheen)" }} />

            {isNearDone && (
              <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2.5 py-1 rounded-full mb-2 relative"
                style={{ background: "rgba(255,255,255,0.2)", color: "#fff" }}>
                <i className="ri-fire-line" />Almost there
              </span>
            )}

            <p className="text-xs mb-1 relative" style={{ color: "var(--gradient-hero-text-faint)" }}>Saved so far</p>
            <p className="text-white font-bold text-3xl font-display relative">₦{Number(goal.currentAmount).toLocaleString()}</p>
            <p className="text-xs mt-1 relative" style={{ color: "var(--gradient-hero-text-muted)" }}>
              of ₦{Number(goal.targetAmount).toLocaleString()} · {pct}%
            </p>

            <div className="h-2.5 bg-white/20 rounded-full overflow-hidden mt-3 relative">
              <div
                className="h-full bg-white rounded-full transition-all duration-700"
                style={{ width: `${Math.min(pct, 100)}%`, boxShadow: isNearDone ? "0 0 14px rgba(255,255,255,0.6)" : "none" }}
              />
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div className="bg-surface border border-border rounded-2xl p-3">
            <p className="text-faint text-xs">Days remaining</p>
            <p className="text-primary font-bold">{daysLeft != null ? daysLeft : "—"}</p>
          </div>
          <div className="bg-surface border border-border rounded-2xl p-3">
            <p className="text-faint text-xs">Target date</p>
            <p className="text-primary font-bold text-sm">{new Date(goal.endDate).toLocaleDateString("en-NG", { day: "numeric", month: "short", year: "numeric" })}</p>
          </div>
        </div>

        {/* FIXED — new. See AutoSaveStatusCard's header note. */}
        <AutoSaveStatusCard goal={goal} onFix={() => setAction("topup")} />

        {goal.type === "fixed" && !matured && goal.status === "active" && (
          <div className="flex items-start gap-2 px-3 py-2.5 bg-warning-light border border-warning-border rounded-xl">
            <i className="ri-lock-2-line text-sm mt-0.5" style={{ color: "var(--color-warning)" }} />
            <p className="text-xs" style={{ color: "var(--color-warning-text)" }}>
              This is a fixed goal maturing {new Date(goal.endDate).toLocaleDateString("en-NG", { day: "numeric", month: "long" })}.
              You can break it early, but an early-withdrawal penalty applies. Withdraw on or after the maturity date (or a penalty-free day) to avoid it.
            </p>
          </div>
        )}

        {goal.status === "active" && !action && (
          <div className="grid grid-cols-2 gap-3">
            <button onClick={() => setAction("topup")}
              className="bg-brand hover:bg-brand-hover text-on-brand py-3.5 rounded-2xl font-bold text-sm transition-colors flex items-center justify-center gap-1.5">
              <i className="ri-add-line" />Top Up
            </button>
            <button onClick={openWithdraw} disabled={!canWithdraw}
              className="bg-surface border-2 border-border-strong disabled:opacity-40 text-secondary py-3.5 rounded-2xl font-bold text-sm transition-colors flex items-center justify-center gap-1.5">
              <i className="ri-bank-line" />{matured ? "Release" : "Withdraw"}
            </button>
          </div>
        )}

        {action === "topup" && (
          <div className="bg-surface border border-border rounded-2xl p-4 space-y-3">
            <p className="text-secondary font-semibold text-sm">Add to this goal</p>
            <div className="bg-surface-muted border border-border-strong rounded-xl flex items-center px-3">
              <span className="text-muted font-bold mr-2">₦</span>
              <input type="number" value={amount} onChange={e => setAmount(e.target.value)} placeholder="Amount"
                className="flex-1 py-2.5 outline-none text-primary text-sm bg-transparent" autoFocus />
            </div>
            <input type="password" inputMode="numeric" maxLength={4} value={pin}
              onChange={e => setPin(e.target.value.replace(/\D/g, "").slice(0, 4))} placeholder="Transaction PIN"
              className="w-full bg-surface-muted border border-border-strong rounded-xl px-3 py-2.5 text-center tracking-[0.4em] text-sm outline-none text-primary" />
            <div className="flex gap-2">
              <button onClick={() => { setAction(null); setAmount(""); setPin(""); }} className="flex-1 py-2.5 rounded-xl bg-surface-hover text-secondary text-sm font-semibold">Cancel</button>
              <button onClick={submitTopup} disabled={busy} className="flex-1 py-2.5 rounded-xl bg-brand text-on-brand text-sm font-bold">
                {busy ? "..." : "Add"}
              </button>
            </div>
          </div>
        )}

        {action === "withdraw" && (
          <div className="bg-surface border border-border rounded-2xl p-4 space-y-3">
            <p className="text-secondary font-semibold text-sm">Release to your wallet?</p>

            {quote ? (
              <div className="bg-surface-muted border border-border-strong rounded-xl p-3 space-y-1.5 text-sm">
                <div className="flex justify-between"><span className="text-muted">Saved</span><span className="font-semibold text-primary">₦{Number(quote.principal).toLocaleString()}</span></div>
                {quote.interest > 0 && (
                  <div className="flex justify-between"><span className="text-muted">Interest</span><span className="font-semibold" style={{ color: "var(--color-success)" }}>+ ₦{Number(quote.interest).toLocaleString()}</span></div>
                )}
                {quote.charge > 0 && (
                  <div className="flex justify-between">
                    <span className="text-muted">{quote.chargeReason === "flexible_fee" ? "Withdrawal fee" : "Early-withdrawal penalty"}</span>
                    <span className="font-semibold" style={{ color: "var(--color-danger)" }}>− ₦{Number(quote.charge).toLocaleString()}</span>
                  </div>
                )}
                <div className="h-px bg-border" />
                <div className="flex justify-between"><span className="text-secondary font-bold">You receive</span><span className="font-bold text-primary">₦{Number(quote.payout).toLocaleString()}</span></div>
                {quote.charge > 0 && quote.nextPenaltyFreeDate && (
                  <p className="text-[11px] pt-1" style={{ color: "var(--color-warning)" }}>
                    💡 Withdraw free on {new Date(quote.nextPenaltyFreeDate).toLocaleDateString("en-NG", { day: "numeric", month: "long" })} to avoid the penalty.
                  </p>
                )}
              </div>
            ) : (
              <div className="h-16 bg-skeleton rounded-xl animate-pulse" />
            )}

            <input type="password" inputMode="numeric" maxLength={4} value={pin}
              onChange={e => setPin(e.target.value.replace(/\D/g, "").slice(0, 4))} placeholder="Transaction PIN"
              className="w-full bg-surface-muted border border-border-strong rounded-xl px-3 py-2.5 text-center tracking-[0.4em] text-sm outline-none text-primary" autoFocus />
            <div className="flex gap-2">
              <button onClick={() => { setAction(null); setPin(""); setQuote(null); }} className="flex-1 py-2.5 rounded-xl bg-surface-hover text-secondary text-sm font-semibold">Cancel</button>
              <button onClick={submitWithdraw} disabled={busy || !quote} className="flex-1 py-2.5 rounded-xl bg-brand text-on-brand text-sm font-bold">
                {busy ? "..." : "Confirm"}
              </button>
            </div>
          </div>
        )}

        {goal.status !== "active" && (
          <div className="bg-surface border border-border rounded-2xl p-4 text-center">
            <i className="ri-checkbox-circle-line text-2xl" style={{ color: "var(--color-success)" }} />
            <p className="text-secondary font-semibold text-sm mt-1">Goal {goal.status}</p>
            {goal.releasedAmount != null && (
              <p className="text-faint text-xs mt-1">
                ₦{Number(goal.releasedAmount).toLocaleString()} released
                {goal.interestPaid > 0 ? ` (incl. ₦${Number(goal.interestPaid).toLocaleString()} interest)` : ""}
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}